import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Package,
  Activity,
  DollarSign,
  RefreshCw,
} from 'lucide-react';
import Card from '../common/Card';
import { useSocketContext } from '../../App';
import { useApi } from '../../hooks/useApi';
import { getOverview, getTransactionSummary, getLowStock, getStockHealth } from '../../api/client';
import StockOverview from './StockOverview';
import TransactionFeed from './TransactionFeed';
import PopularItems from './PopularItems';
import ZoneHealth from './ZoneHealth';

function StatCard({ title, value, icon, change, changeLabel, glow, loading }) {
  return (
    <Card title={title} icon={icon} glow={glow} loading={loading}>
      <div className="flex items-end justify-between">
        <span className="text-2xl font-bold font-mono text-text-primary glow-green">
          {typeof value === 'number' ? value.toLocaleString() : value ?? '—'}
        </span>
        {change !== undefined && (
          <div
            className={`flex items-center gap-1 text-xs font-mono ${
              change >= 0 ? 'text-accent-green' : 'text-accent-red'
            }`}
          >
            {change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>
              {change >= 0 ? '+' : ''}
              {change}%
            </span>
          </div>
        )}
      </div>
      {changeLabel && (
        <p className="text-[11px] text-text-muted mt-1 font-mono">{changeLabel}</p>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const socket = useSocketContext();
  const { data: overview, loading: overviewLoading, refetch: refetchOverview } = useApi(getOverview);
  const { data: summary, loading: summaryLoading, refetch: refetchSummary } = useApi(getTransactionSummary);
  const { data: lowStock, loading: lowStockLoading } = useApi(getLowStock);
  const { data: health, loading: healthLoading } = useApi(getStockHealth);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetchOverview();
      refetchSummary();
    }, 30000);
    return () => clearInterval(interval);
  }, [refetchOverview, refetchSummary]);

  const totalTransactions = overview?.totalTransactions ?? summary?.total ?? 0;
  const activeZones = overview?.activeZones ?? 7;
  const lowStockCount = Array.isArray(lowStock) ? lowStock.length : lowStock?.count ?? 0;
  const totalValue = overview?.totalValue ?? 0;

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Transactions"
          value={totalTransactions}
          icon={Activity}
          change={overview?.transactionChange}
          changeLabel="vs. yesterday"
          glow="green"
          loading={overviewLoading && summaryLoading}
        />
        <StatCard
          title="Active Zones"
          value={activeZones}
          icon={Package}
          changeLabel="trader zones online"
          glow="blue"
          loading={overviewLoading}
        />
        <StatCard
          title="Low Stock Alerts"
          value={lowStockCount}
          icon={AlertTriangle}
          changeLabel="items below threshold"
          glow={lowStockCount > 0 ? 'red' : 'none'}
          loading={lowStockLoading}
        />
        <StatCard
          title="Stock Value"
          value={totalValue > 0 ? `₽${(totalValue / 1000).toFixed(1)}K` : '—'}
          icon={DollarSign}
          change={overview?.valueChange}
          changeLabel="estimated total"
          glow="green"
          loading={overviewLoading}
        />
      </div>

      {/* Middle Row: Stock Chart + Transaction Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <StockOverview />
        </div>
        <div className="lg:col-span-1">
          <TransactionFeed />
        </div>
      </div>

      {/* Bottom Row: Popular Items + Zone Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PopularItems />
        <ZoneHealth />
      </div>
    </div>
  );
}
